import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { api } from "@/helper/api";
import { RootState } from "@/components/state/store";
import { buildEntityRequestParameters } from "@/components/tables/fields/entityRequestParameters";
import { useEntityRequestFailureHandler } from "@/components/tables/fields/useEntityRequestFailureHandler";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";

export interface SelectFieldItemsInput {
  field: Pick<
    CrudModalField,
    | "id"
    | "entity"
    | "multiselect"
    | "selectOptions"
    | "userEntityValueField"
    | "useGlobalStateToImpersonate"
    | "additionalUrlParameters"
    | "getOnlyActiveUsers"
    | "additionalRoutePartsforGet"
  >;
  companySubjectId?: string;
  subjectIdForBuildings?: unknown;
  limit?: string;
  handleError?: (error: any) => void;
}

const toPreselectedIds = (preselectedValue: any) => {
  const selectedIds = [];
  for (const key in preselectedValue) {
    selectedIds.push(
      preselectedValue[key].id ? preselectedValue[key].id : preselectedValue[key]
    );
  }
  return selectedIds;
};

const toPreselectedSingleValue = (
  preselectedValue: any,
  field: Pick<CrudModalField, "entity" | "userEntityValueField">
) => {
  if (!preselectedValue.id) return preselectedValue;
  if (field.entity === "user" && field.userEntityValueField === "subject_id") {
    return preselectedValue.subject_id ?? preselectedValue.id;
  }
  return preselectedValue.id;
};

export const useSelectFieldItems = ({
  field,
  companySubjectId,
  subjectIdForBuildings,
  limit,
  handleError,
}: SelectFieldItemsInput) => {
  const name = field.id as string;
  const { getValues, setValue } = useFormContext();
  const globalSubject = useSelector((state: RootState) => state.subjectSearch);
  const handleEntityRequestFailure = useEntityRequestFailureHandler(handleError);
  const [entityItems, setEntityItems] = useState<any[]>([]);
  const [itemsLoaded, setItemsLoaded] = useState<boolean>(false);

  const entityRequest = buildEntityRequestParameters(field, {
    subdomain: window.location.hostname.split(".")[0],
    globalSubjectValue: globalSubject?.value,
    companySubjectId,
    subjectIdForBuildings,
    limit,
  });
  const entityRequestKey = JSON.stringify([
    field.entity,
    entityRequest.parametersToRender,
    entityRequest.additionalRouteParts,
  ]);
  const optionsLoadedBefore = useRef(false);
  useEffect(() => {
    if (!field.entity) return;
    let superseded = false;
    setItemsLoaded(false);
    api.genericApiRequest({
      method: "get",
      entity: field.entity,
      parametersToRender: entityRequest.parametersToRender,
      additionalRouteParts: entityRequest.additionalRouteParts,
      getAllPages: true,
      successHandler: (res: any) => {
        if (superseded) return;
        const data = res.data;
        setEntityItems(data.results);
        const preselection = optionsLoadedBefore.current
          ? undefined // new request parameters (e.g. another company) invalidate the previous selection
          : getValues(name); // read on arrival: a reopened edit modal mounts before the row values reach the form
        optionsLoadedBefore.current = true;
        if (field.multiselect) {
          if (data.next === null) {
            setValue(name, toPreselectedIds(preselection));
            setItemsLoaded(true);
          }
        } else {
          setValue(
            name,
            preselection ? toPreselectedSingleValue(preselection, field) : ""
          );
          setItemsLoaded(true);
        }
      },
      failHandler: (error: any) => {
        if (superseded) return;
        handleEntityRequestFailure(error);
      },
    });
    return () => {
      superseded = true;
    };
  }, [entityRequestKey]);

  useEffect(() => {
    if (field.entity || !field.selectOptions) return;
    setItemsLoaded(true);
  }, [field.selectOptions]);

  return {
    items: field.selectOptions ? field.selectOptions : entityItems,
    itemsLoaded,
  };
};
